/**
 * Backend-timestamp parsing helpers.
 *
 * The backend serializes some datetime columns (notably `position_time`)
 * without a timezone suffix because the underlying SQLAlchemy column is
 * `DateTime` (naive). Per the ECMAScript spec a date-time literal without
 * an offset should be parsed as UTC, but historically several browsers
 * have interpreted it as local time. To keep behaviour deterministic
 * across browsers we explicitly normalise naive ISO strings to UTC
 * before handing them to `Date.parse`.
 */

const HAS_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/;

/**
 * Append a `Z` (UTC) suffix to an ISO datetime that lacks any timezone
 * marker. Date-only values (`YYYY-MM-DD`) are left untouched — they are
 * already unambiguously UTC per spec.
 *
 * Exported for unit tests; prefer {@link toUtcEpochMs} in app code.
 */
export function normalizeToUtcIso(iso: string): string {
    // Only treat strings as "datetimes" once they contain a time
    // component. A bare `YYYY-MM-DD` is spec-defined to be UTC already.
    if (!iso.includes("T")) return iso;
    if (HAS_OFFSET.test(iso)) return iso;
    return `${iso}Z`;
}

/**
 * Parse a backend ISO timestamp to a UTC epoch-millisecond number.
 *
 * Returns `null` for `null` / `undefined` / unparseable input so callers
 * can keep a "missing position" sentinel without sprinkling `NaN` checks.
 */
export function toUtcEpochMs(
    iso: string | null | undefined,
): number | null {
    if (iso === null || iso === undefined || iso === "") return null;
    const ms = Date.parse(normalizeToUtcIso(iso));
    return Number.isFinite(ms) ? ms : null;
}

const DTG_MONTHS = [
    "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
    "JUL", "AUG", "SEP", "OCT", "NOV", "DEC",
];

/**
 * Format a timestamp as a NATO Date-Time Group: `DDHHMMSSZMMMYYYY`.
 *
 * Accepts either a backend ISO string (naive strings are normalised to
 * UTC first) or a UTC epoch-millisecond number (as carried on CoT
 * `sendTime`/`startTime`). All components are UTC — the trailing `Z`
 * denotes Zulu time, so local getters must never be used here.
 *
 * Shared by the REST fetch paths (mapSlice / markersSlice) and the live
 * MQTT parse path (cotToMapEntity) so the on-symbol time is identical
 * regardless of how the entity reached the store.
 */
export function formatDtg(input: string | number): string {
    const d = typeof input === "number"
        ? new Date(input)
        : new Date(normalizeToUtcIso(input));
    const dd = String(d.getUTCDate()).padStart(2, "0");
    const HH = String(d.getUTCHours()).padStart(2, "0");
    const MM = String(d.getUTCMinutes()).padStart(2, "0");
    const SS = String(d.getUTCSeconds()).padStart(2, "0");
    const MMM = DTG_MONTHS[d.getUTCMonth()];
    const YYYY = d.getUTCFullYear();
    return `${dd}${HH}${MM}${SS}Z${MMM}${YYYY}`;
}
